import i18n from "@/i18n";

/**
 * Cria um novo objeto de estado vazio com propriedades padrão.
 *
 * @returns Um objeto de estado vazio com valores padrão.
 *
 * @property {Array} logs - Uma lista de logs.
 * @property {boolean} showDialog - Indica se um diálogo deve ser exibido (o padrão é `false`).
 * @property {Object} itemOnEmailClick - O item clicado no icone de email tabela (o padrão é um objeto vazio).
 * @property {boolean} showToast - Indica se um toast/notificação deve ser exibido (o padrão é `false`).
 * @property {boolean} isLoadingTableData - Indica se os dados da tabela estão sendo carregados (o padrão é `false`).
 */
const StateEmpty = function()
{
	return {
		logs:                [],
		showDialog:          false,
		itemOnEmailClick:    {},
		itemOnExpandedPanel: [],
		showToast:           false,
		isLoadingTableData:  false,
		search:              "",
	};
};

/**
 * Cria um novo objeto de visualização de log vazio com propriedades padrão.
 *
 * @returns Um objeto de visualização de log com valores padrão.
 * @property {string} userName - Nome do usuário.
 * @property {string} generatingUnitName - Nome da unidade geradora.
 * @property {string} frequencyTrendName - Nome da tendência de frequência.
 * @property {string} dateTime - Data e hora da modificação.
 * @property {number} limitsModificationType - Tipo de modificação de limites
 * (null por padrão que indica que não há nenhum tipo de modificação).
 * @property {number} notificationsModificationType - Tipo de modificação de notificações
 * (null por padrão que indica que não há nenhum tipo de modificação).
 * @property {number} previousAlarm - Valor anterior do alarm (0 por padrão).
 * @property {number} previousTrip - Valor anterior da trip (0 por padrão).
 * @property {number} previousLimitMaxNotifications - Valor máximo anterior de notificações (0 por padrão).
 * @property {number} previousLimitIntervalHours - Intervalo de horas anterior para o limite (0 por padrão).
 * @property {number} alarm - Valor atual do alarm (0 por padrão).
 * @property {number} trip - Valor atual da trip (0 por padrão).
 * @property {number} limitMaxNotifications - Valor máximo atual de notificações (0 por padrão).
 * @property {number} limitIntervalHours - Intervalo de horas atual para o limite (0 por padrão).
 * @property {Array} tripEmailRecipients - Lista de destinatários de emails de trip.
 * @property {Array} alarmEmailRecipients - Lista de destinatários de emails de alarm.
 * @property {boolean} hasEmailToShow - Indica se há emails a serem exibidos (false por padrão).
 **/
const LogViewEmpty = function()
{
	return {
		userName:                      "",
		generatingUnitName:            "",
		frequencyTrendName:            "",
		dateTime:                      "",
		limitsModificationType:        null,
		notificationsModificationType: null,
		previousAlarm:                 0,
		previousTrip:                  0,
		previousLimitMaxNotifications: 0,
		previousLimitIntervalHours:    0,
		alarm:                         0,
		trip:                          0,
		limitMaxNotifications:         0,
		limitIntervalHours:            0,
		tripEmailRecipients:           [],
		alarmEmailRecipients:          [],
		hasEmailToShow:                false,
	};
};

/**
 * Cria um novo objeto de visualização de log com propriedades padrão.
 *
 * Esta função gera um objeto que representa uma entrada de log com valores predefinidos.
 * Se o parâmetro `view` for fornecido, ele será utilizado;
 * caso contrário, a função utiliza `LogViewEmpty()` como padrão.
 * @param view - Um objeto opcional para inicializar a visualização do log.
 * @param index - Indice do mapeamento do array de logs, necessário para propriedade de expansão da tabela.
 *
 * @returns Um objeto de visualização de log com propriedades padrão.
 */
const LogView = function(view = LogViewEmpty(), index = 0)
{
	const logView = {
		id:                            index,
		userName:                      view.userName,
		generatingUnitName:            view.generatingUnitName,
		frequencyTrendName:            view.frequencyTrendName,
		dateTime:                      view.dateTime,
		limitsModificationType:        view.limitsModificationType,
		notificationsModificationType: view.notificationsModificationType,
		previousAlarm:                 view.previousAlarm,
		previousTrip:                  view.previousTrip,
		previousLimitMaxNotifications: view.previousLimitMaxNotifications,
		previousLimitIntervalHours:    view.previousLimitIntervalHours,
		previousDebounceTriggerTrend:  view.previousDebounceTriggerTrend,
		alarm:                         view.alarm,
		trip:                          view.trip,
		limitMaxNotifications:         view.limitMaxNotifications,
		limitIntervalHours:            view.limitIntervalHours,
		debounceTriggerTrend:          view.debounceTriggerTrend,
		tripEmailRecipients:           view.tripEmailRecipients,
		alarmEmailRecipients:          view.alarmEmailRecipients,
		hasEmailToShow:                false
	};

	const hasTripRecipients = view.tripEmailRecipients && view.tripEmailRecipients.length > 0;
	const hasAlarmRecipients = view.alarmEmailRecipients && view.alarmEmailRecipients.length > 0;

	// Informa se a view deve renderizar o icone de e-mail.
	logView.hasEmailToShow = hasAlarmRecipients || hasTripRecipients;

	return logView;
};

/**
 * Cria as configuração (array de objetos) das colunas da tabela.
 * Onde cada objeto representa uma coluna da tabela.
 *
 * @returns Um array de objetos que configuram as colunas da tabela.
 **/
const HeaderTableView = function()
{
	return [
		{
			text:  i18n.t("TableHeaders.Date"), value: "dateTime", align: "center",
		},
		{
			text:  i18n.t("TableHeaders.User"), value: "userName", align: "center",
		},
		{
			text:  i18n.t("Labels.Turbines"), value: "generatingUnitName", align: "center",
		},
		{
			text:  i18n.t("Labels.Signal"), value: "frequencyTrendName", align: "center",
		},
		{
			text:  i18n.t("TableHeaders.Emails"), value: "hasEmailToShow", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.Details"), value: "data-table-expand", align: "center"
		},
	];
};

/**
 * Retorna um objeto com os valores padrão para uma visualização de emails modificados vazia.
 *
 * @returns Objeto contendo os campos padrão de uma visualização de emails modificados.
 * @property {Array} alarmEmails - Lista de emails associados a alarm.
 * @property {Array} tripEmails - Lista de emails associados a trip.
 * @property {boolean} areTheyEquals - Indica se as listas de emails de alarme e de viagem são iguais
 * (false por padrão).
 * @property {boolean} hasEmails - Indica se há emails para exibir (false por padrão).
 */
const ModifiedEmailsViewEmpty = function()
{
	return {
		alarmEmails:   [],
		tripEmails:    [],
		areTheyEquals: false,
		hasEmails:     false,
	};
};

export default {
	StateEmpty,
	LogView,
	HeaderTableView,
	ModifiedEmailsViewEmpty
};